import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type ConfirmationButtonStyle = 'primary' | 'secondary' | 'danger' | 'success';

export interface ConfirmationModalOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmButtonStyle?: ConfirmationButtonStyle;
  showCancel?: boolean;
}

interface ConfirmationModalState {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmButtonStyle: ConfirmationButtonStyle;
  showCancel: boolean;
  resolve: (value: boolean) => void;
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmationModalService {
  private readonly stateSubject = new BehaviorSubject<ConfirmationModalState | null>(null);

  get state$(): Observable<ConfirmationModalState | null> {
    return this.stateSubject.asObservable();
  }

  confirm(options: ConfirmationModalOptions): Promise<boolean> {
    return this.open({
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'primary',
      showCancel: true,
      ...options,
    });
  }

  async alert(options: Omit<ConfirmationModalOptions, 'showCancel'>): Promise<void> {
    await this.open({
      confirmLabel: 'OK',
      cancelLabel: 'Close',
      confirmButtonStyle: 'primary',
      showCancel: false,
      ...options,
    });
  }

  resolve(result: boolean): void {
    const state = this.stateSubject.getValue();

    if (!state) {
      return;
    }

    state.resolve(result);
    this.stateSubject.next(null);
  }

  private open(options: Required<Omit<ConfirmationModalState, 'resolve'>>): Promise<boolean> {
    const current = this.stateSubject.getValue();

    if (current) {
      current.resolve(false);
      this.stateSubject.next(null);
    }

    return new Promise<boolean>((resolve) => {
      this.stateSubject.next({
        ...options,
        resolve,
      });
    });
  }
}
